import React, { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, RotateCcw, X } from 'lucide-react';
import ProductCard from '../products/ProductCard';
import { DbProduct } from '../../utils/api';

const BASE = `${window.location.protocol}//${window.location.host}`;

interface Message {
    role: 'user' | 'assistant';
    content: string;
}

interface HeroChatbotProps {
    asModal?: boolean;
    onClose?: () => void;
    onSearchComplete?: (products: DbProduct[], hasSearched: boolean) => void;
}

const searchProducts = async (text: string): Promise<DbProduct[]> => {
    const lower = text.toLowerCase();

    const clubs: [string, string][] = [
        ['tus hoberge', 'TUS Hoberge'], ['vfr wellensiek', 'VFR Wellensiek'],
        ['sc bielefeld', 'SC Bielefeld'], ['vfl bochum', 'VFL Bochum'],
        ['vfl osnabrück', 'VFL Osnabrück'], ['tus', 'TUS'], ['vfr', 'VFR'],
        ['tsv', 'TSV'], ['vfl', 'VFL'], [' fc ', 'FC'], [' sv ', 'SV'], [' sc ', 'SC'],
    ];
    let detectedClub = '';
    for (const [kw, val] of clubs) {
        if (lower.includes(kw)) { detectedClub = val; break; }
    }

    const productTypes: [string, string][] = [
        ['schuhe', 'Schuhe'], ['schuh', 'Schuhe'], ['trikot', 'Trikot'],
        ['jersey', 'Trikot'], ['pullover', 'Pullover'], ['hoodie', 'Pullover'],
        ['pulli', 'Pullover'], ['hose', 'Hose'], ['short', 'Hose'],
        ['jacke', 'Jacke'], ['ball', 'Ball'], ['tasche', 'Tasche'],
        ['stutzen', 'Stutzen'], ['torwart', 'Torwarttrikot'],
    ];
    let detectedProductType = '';
    for (const [kw, val] of productTypes) {
        if (lower.includes(kw)) { detectedProductType = val; break; }
    }

    const sizeMatch =
        lower.match(/gr(?:öße?|\.)\s*:?\s*([a-z0-9]+)/i) ||
        lower.match(/size\s+([a-z0-9]+)/i) ||
        lower.match(/\b(xs|s|m|l|xl|xxl|3xl|4xl|[3-9]\d|1[0-9]{2}|2[0-9]{2})\b/i);
    const detectedSize = sizeMatch ? sizeMatch[1].toUpperCase() : '';

    const isKids = lower.includes('kinder') || lower.includes('kind');
    let detectedCategory = '';
    if (!isKids) {
        const categories: [string, string][] = [
            ['männer', 'Männer'], ['herren', 'Männer'], ['frauen', 'Frauen'],
            ['damen', 'Frauen'], ['jungen', 'Jungen'], ['boys', 'Jungen'],
            ['mädchen', 'Mädchen'], ['girls', 'Mädchen'], ['equipment', 'Equipment'],
        ];
        for (const [kw, val] of categories) {
            if (lower.includes(kw)) { detectedCategory = val; break; }
        }
    }

    const baseParams = new URLSearchParams();
    if (detectedClub) baseParams.append('club', detectedClub);
    if (detectedProductType) baseParams.append('product_type', detectedProductType);
    if (detectedSize) baseParams.append('size', detectedSize);
    baseParams.append('limit', '12');

    if (isKids) {
        const boysParams = new URLSearchParams(baseParams);
        boysParams.set('category', 'Jungen');
        const girlsParams = new URLSearchParams(baseParams);
        girlsParams.set('category', 'Mädchen');
        const [boys, girls] = await Promise.all([
            fetch(`${BASE}/api/products.php?${boysParams}`).then(r => r.json()),
            fetch(`${BASE}/api/products.php?${girlsParams}`).then(r => r.json()),
        ]);
        return [
            ...(boys.success && Array.isArray(boys.data) ? boys.data : []),
            ...(girls.success && Array.isArray(girls.data) ? girls.data : []),
        ];
    }

    if (detectedCategory) baseParams.append('category', detectedCategory);

    if (!detectedClub && !detectedProductType && !detectedSize && !detectedCategory) {
        const clean = text
            .replace(/\b(hast|du|was|vom|von|der|die|das|ein|eine|einen|ich|suche|gibt|es|beim|bei|für|nach)\b/gi, '')
            .replace(/\s+/g, ' ').trim();
        if (clean) baseParams.append('search', clean);
    }

    const res = await fetch(`${BASE}/api/products.php?${baseParams}`);
    const data = await res.json();
    return data.success && Array.isArray(data.data) ? data.data : [];
};

const INITIAL_MESSAGE = '👋 Hallo! Ich bin dein SportyReplay Assistent.\n\nWonach suchst du? Du kannst mir einfach beschreiben was du brauchst – z.B. „Trikot für deinen Verein Größe M" oder „Schuhe für Kinder".';

function renderMessage(text: string, linkClass: string): React.ReactNode[] {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const result: React.ReactNode[] = [];
    let lastIndex = 0;
    let match;
    while ((match = linkRegex.exec(text)) !== null) {
        if (match.index > lastIndex) result.push(text.slice(lastIndex, match.index));
        result.push(
            <a key={match.index} href={match[2]} className={linkClass}>
                {match[1]}
            </a>
        );
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < text.length) result.push(text.slice(lastIndex));
    return result;
}

const HeroChatbot: React.FC<HeroChatbotProps> = ({ asModal = false, onClose, onSearchComplete }) => {
    const [messages, setMessages] = useState<Message[]>([{ role: 'assistant', content: INITIAL_MESSAGE }]);
    const [products, setProducts] = useState<DbProduct[]>([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Im Modal-Modus: Suchergebnisse an Parent melden
    useEffect(() => {
        if (asModal && onSearchComplete && !isLoading) {
            onSearchComplete(products, hasSearched);
        }
    }, [products, hasSearched, isLoading]); // eslint-disable-line

    // Im Modal-Modus: Input sofort fokussieren
    useEffect(() => {
        if (asModal) {
            setTimeout(() => inputRef.current?.focus(), 150);
        }
    }, [asModal]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isLoading]);

    const sendMessage = async () => {
        if (!input.trim() || isLoading) return;
        const text = input.trim();
        const userMsg: Message = { role: 'user', content: text };
        const updatedMessages = [...messages, userMsg];
        setMessages(updatedMessages);
        setInput('');
        setIsLoading(true);
        setHasSearched(true);
        try {
            const [chatRes, found] = await Promise.all([
                fetch(`${BASE}/api/chatbot.php`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ message: text, history: messages })
                }),
                searchProducts(text)
            ]);
            const chatData = await chatRes.json();
            const reply = chatData.success && chatData.reply
                ? chatData.reply
                : 'Entschuldigung, da ist etwas schiefgelaufen. Bitte versuche es nochmal.';
            setMessages([...updatedMessages, { role: 'assistant', content: reply }]);
            setProducts(found);
        } catch {
            setMessages([...updatedMessages, { role: 'assistant', content: 'Verbindungsfehler. Bitte prüfe deine Internetverbindung.' }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
    };

    const reset = () => {
        setMessages([{ role: 'assistant', content: INITIAL_MESSAGE }]);
        setProducts([]);
        setInput('');
        setHasSearched(false);
        if (!asModal) {
            setTimeout(() => inputRef.current?.focus(), 100);
        }
    };

    const lastIsAssistant = messages[messages.length - 1]?.role === 'assistant';

    // ── MODAL-Modus (Mobile) ────────────────────────────────────────────────
    if (asModal) {
        return (
            <div className="h-full flex flex-col bg-gradient-to-br from-orange-500 via-orange-600 to-red-600">
                {/* Header mit Schließen-Button */}
                <div className="flex items-center justify-between px-4 py-3 flex-shrink-0 border-b border-white/20">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-white/80" />
                        <span className="text-white font-bold text-sm">KI-Assistent</span>
                        <Sparkles className="w-4 h-4 text-white/80" />
                    </div>
                    <button
                        onClick={onClose}
                        className="text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/20 transition-colors"
                        aria-label="Schließen"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Chat-Nachrichten */}
                <div
                    data-testid="chatbot-messages"
                    className="flex-1 min-h-0 overflow-y-auto mx-3 my-2 bg-white/10 rounded-xl px-3 py-2 space-y-2"
                >
                    {messages.map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            {msg.role === 'assistant' && (
                                <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center text-sm flex-shrink-0 mr-2 mt-0.5">🤖</div>
                            )}
                            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                                msg.role === 'user' ? 'bg-white text-orange-700 font-medium rounded-tr-sm' : 'bg-white/20 text-white rounded-tl-sm'
                            }`}>
                                {msg.role === 'assistant' ? renderMessage(msg.content, 'underline text-white/90 hover:text-white') : msg.content}
                            </div>
                        </div>
                    ))}
                    {isLoading && (
                        <div className="flex justify-start">
                            <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center text-sm flex-shrink-0 mr-2">🤖</div>
                            <div className="bg-white/20 rounded-2xl rounded-tl-sm px-4 py-3">
                                <div className="flex gap-1 items-center">
                                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {/* Eingabefeld */}
                <div className="px-3 pb-3 pt-1 flex-shrink-0">
                    <div className="flex gap-2">
                        <input
                            ref={inputRef}
                            type="text"
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Hier tippen..."
                            disabled={isLoading}
                            style={{ fontSize: '16px' }}
                            className="flex-1 px-4 py-3 rounded-xl bg-white/95 text-gray-800 focus:outline-none focus:ring-2 focus:ring-white shadow-lg disabled:opacity-70 placeholder-gray-400"
                        />
                        <button
                            onClick={sendMessage}
                            disabled={!input.trim() || isLoading}
                            className="bg-white text-orange-600 p-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        >
                            <Send className="w-5 h-5" />
                        </button>
                        {hasSearched && (
                            <button onClick={reset} title="Neue Suche" className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-xl transition-colors">
                                <RotateCcw className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // ── DESKTOP-Modus (normal) ───────────────────────────────────────────────
    return (
        <div className="mb-4 md:mb-12">
            {/* ── Desktop: voller oranger Banner ── */}
            <div className="hidden md:block bg-gradient-to-br from-orange-500 via-orange-600 to-red-600 rounded-2xl overflow-hidden shadow-2xl">
                {/* Header */}
                <div className="px-6 pt-5 pb-3 text-center">
                    <div className="flex items-center justify-center gap-2 mb-0.5">
                        <Sparkles className="w-4 h-4 text-white/80" />
                        <span className="text-white/90 text-sm font-medium">KI-Assistent</span>
                        <Sparkles className="w-4 h-4 text-white/80" />
                    </div>
                    <h1 className="text-3xl font-extrabold text-white leading-tight">Was suchst du?</h1>
                </div>

                {/* Chat-Nachrichten */}
                <div
                    className="mx-4 mb-3 bg-white/10 rounded-xl px-4 py-3 max-h-64 overflow-y-auto space-y-3"
                >
                    {messages.map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            {msg.role === 'assistant' && (
                                <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center text-sm flex-shrink-0 mr-2 mt-0.5">🤖</div>
                            )}
                            <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
                                msg.role === 'user' ? 'bg-white text-orange-700 font-medium rounded-tr-sm' : 'bg-white/20 text-white rounded-tl-sm'
                            }`}>
                                {msg.role === 'assistant' ? renderMessage(msg.content, 'underline text-white/90 hover:text-white') : msg.content}
                            </div>
                        </div>
                    ))}
                    {isLoading && (
                        <div className="flex justify-start">
                            <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center text-sm flex-shrink-0 mr-2">🤖</div>
                            <div className="bg-white/20 rounded-2xl rounded-tl-sm px-4 py-3">
                                <div className="flex gap-1 items-center">
                                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                                    <span className="w-1.5 h-1.5 bg-white rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef} />
                </div>

                {lastIsAssistant && messages.length > 1 && !isLoading && (
                    <div className="px-5 pb-1">
                        <p className="text-xs text-white/70 animate-pulse">↓ Tippe deine Antwort</p>
                    </div>
                )}

                {/* Eingabe Desktop */}
                <div className="px-4 pb-4">
                    <div className="flex gap-2">
                        <input
                            ref={inputRef}
                            type="text"
                            value={input}
                            onChange={e => setInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder={lastIsAssistant && messages.length > 1 ? 'Hier antworten...' : 'z.B. Trikot für deinen Verein Größe M...'}
                            disabled={isLoading}
                            className="flex-1 px-4 py-3 rounded-xl bg-white/95 text-gray-800 text-base focus:outline-none focus:ring-2 focus:ring-white shadow-lg disabled:opacity-70 placeholder-gray-400"
                        />
                        <button
                            onClick={sendMessage}
                            disabled={!input.trim() || isLoading}
                            className="bg-white text-orange-600 p-3 rounded-xl font-bold shadow-lg hover:shadow-xl transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                        >
                            <Send className="w-5 h-5" />
                        </button>
                        {hasSearched && (
                            <button onClick={reset} title="Neue Suche" className="bg-white/20 hover:bg-white/30 text-white p-3 rounded-xl transition-colors">
                                <RotateCcw className="w-5 h-5" />
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* Suchergebnisse (Desktop) */}
            {hasSearched && !isLoading && (
                <div className="mt-6">
                    {products.length > 0 ? (
                        <>
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg md:text-xl font-bold text-gray-800">
                                    {products.length} {products.length === 1 ? 'Produkt' : 'Produkte'} gefunden
                                </h2>
                                <button onClick={reset} className="text-sm text-orange-600 hover:text-orange-700 font-medium transition-colors">
                                    Suche zurücksetzen
                                </button>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
                                {products.map(product => <ProductCard key={product.id} {...product} />)}
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-10 bg-gray-50 rounded-2xl border border-gray-200">
                            <div className="text-4xl mb-3">🔍</div>
                            <p className="text-gray-600 font-medium">Keine passenden Produkte gefunden.</p>
                            <p className="text-gray-400 text-sm mt-1">Versuche es mit anderen Suchbegriffen.</p>
                            <button onClick={reset} className="mt-4 text-sm text-orange-600 hover:text-orange-700 font-medium">
                                Neue Suche starten
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default HeroChatbot;
